import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet, Linking } from 'react-native';
import { globalStyles } from '../styles/globalStyles';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

export function SubscriptionSuccess({ navigation }) {
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    console.log("VERIFYING SUBSCRIPTION");
    const verifySubscription = async () => {
      try {
        const auth = getAuth();
        const userId = auth.currentUser?.uid;
        
        if (userId) {
          const db = getFirestore();
          const userRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.subscriptionStatus === 'active') {
              setIsSubscribed(true);
            } else {
              // If not subscribed after 5 seconds, redirect to Plans
              console.log("NOT SUBSCRIBED");
              setError("Something went wrong. Please try again.");
              setTimeout(() => {
                if (!userData.subscriptionStatus === 'active') {
                  navigation.replace('Plans');
                }
              }, 5000);
            }
          }
        }
      } catch (error) {
        console.error('Error verifying subscription status:', error);
        // On error, redirect to Plans after 5 seconds
        setTimeout(() => {
          navigation.replace('Plans');
        }, 5000);
      } finally {
        setIsVerifying(true);
      }
    };

    verifySubscription();
  }, []);

  const handleContinue = () => {
    navigation.navigate('Home');
  };


  if (error) {
    return (
      <View style={globalStyles.container}>
        <BackgroundVideo />
        <View style={[globalStyles.overlay, globalStyles.plansContent]}>
          <View style={styles.errorContainer}>
            <Text style={styles.errorTitle}>Subscription Error</Text>
            
            <View style={styles.errorCard}>
              <Text style={styles.errorMessage}>
                {error}
              </Text>
              <Text style={styles.errorHelp}>
                Don't worry! If you've been charged, your payment is safe. Our team has been notified and will resolve this shortly.
              </Text>
              <View style={styles.supportSection}>
                <Text style={styles.supportTitle}>Need help?{" "} <Text onPress={() => navigation.navigate('Contact')} style={[styles.supportText, { textDecorationLine: 'underline', color: '#0bb5df', fontSize: 16 }]}>
                  Contact us
                </Text></Text>
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity 
                style={[globalStyles.subscribeButton, styles.retryButton]}
                onPress={() => navigation.navigate('Plans')}
              >
                <Text style={globalStyles.subscribeButtonText}>Return to Plans</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={[globalStyles.subscribeButton, styles.homeButton]}
                onPress={() => navigation.navigate('Home')}
              >
                <Text style={[globalStyles.subscribeButtonText, { color: 'white' }]}>Go to Home</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    );
  }

  if (isVerifying && !isSubscribed) {
    return (
      <View style={globalStyles.container}>
        <BackgroundVideo />
        <View style={[globalStyles.overlay, globalStyles.plansContent]}>
          <View style={globalStyles.planCard}>
            <ActivityIndicator size="large" color="#fff" />
            <Text style={[globalStyles.featureText, { textAlign: 'center', marginTop: 20 }]}>
              Verifying your subscription...
            </Text>
          </View>
        </View>
      </View>
    );
  }



  return (
    <View style={globalStyles.container}>
      <BackgroundVideo />
      <View style={[globalStyles.overlay, globalStyles.plansContent]}>
        <View style={globalStyles.planCard}>
          <View style={globalStyles.planHeader}>
            <Text style={[globalStyles.planName, { fontSize: 32, marginBottom: 30 }]}>
              🎉 Welcome to Premium!
            </Text>
            <Text style={[globalStyles.featureText, { textAlign: 'center', marginBottom: 20 }]}>
              Your subscription has been successfully activated.
            </Text>
          </View>

          <View style={globalStyles.featuresContainer}>
            <Text style={globalStyles.featuresTitle}>You now have access to:</Text>
            <View style={globalStyles.featureItem}>
              <Text style={globalStyles.featureIcon}>✓</Text>
              <Text style={globalStyles.featureText}>Save Unlimited Venues</Text>
            </View>
            <View style={globalStyles.featureItem}>
              <Text style={globalStyles.featureIcon}>✓</Text>
              <Text style={globalStyles.featureText}>Create tour folders</Text>
            </View>
            <View style={globalStyles.featureItem}>
              <Text style={globalStyles.featureIcon}>✓</Text>
              <Text style={globalStyles.featureText}>Access more search filters</Text>
            </View>
            <View style={globalStyles.featureItem}>
              <Text style={globalStyles.featureIcon}>✓</Text>
              <Text style={globalStyles.featureText}>Priority Support</Text>
            </View>
            <View style={globalStyles.featureItem}>
              <Text style={globalStyles.featureIcon}>✓</Text>
              <Text style={globalStyles.featureText}>Early Access to New Features</Text>
            </View>
            <View style={globalStyles.featureItem}>
              <Text style={globalStyles.featureIcon}>✓</Text>
              <Text style={globalStyles.featureText}>Premium Venue Filtering</Text>
            </View>
          </View>

          <TouchableOpacity 
            style={globalStyles.subscribeButton}
            onPress={handleContinue}
          >
            <Text style={globalStyles.subscribeButtonText}>Continue to App</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  errorContainer: {
    width: '100%',
    maxWidth: 600,
    padding: 20,
    alignItems: 'center',
  },
  errorTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 32,
    color: '#e74c3c',
    marginBottom: 20,
    textAlign: 'center',
    textShadow: '0px 2px 4px rgba(0,0,0,0.1)',
  },
  errorCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 15,
    padding: 25,
    width: '100%',
    marginBottom: 30,
    borderWidth: 1,
    borderColor: '#e74c3c',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  errorMessage: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 18,
    color: '#e74c3c',
    marginBottom: 15,
    textAlign: 'center',
  },
  errorHelp: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 24,
  },
  supportSection: {
    borderTopWidth: 1,
    borderTopColor: '#eee',
    paddingTop: 20,
    marginTop: 10,
  },
  supportTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 18,
    color: '#333',
    marginBottom: 10,
    textAlign: 'center',
    fontWeight: '600',
  },
  supportText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  supportLink: {
    color: '#0bb5df',
    textDecorationLine: 'underline',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 15,
    width: '100%',
  },
  retryButton: {
    backgroundColor: '#ffffff',
  },
  homeButton: {
    backgroundColor: '#4e7de0',
  },
}); 